import type { OcrReading } from '@/services/ocr-engine'
import { formatRomanNumeral, normalizeRomanNumerals, romanNumeralPattern } from '@/utils/roman-numerals'

export interface MapNameCandidate {
  readonly body: string
  readonly suffix: string
  readonly kind: 'arabic' | 'roman' | 'none' | 'unknown'
  readonly value: string
}

/** Keep the numeral's alphabet: a map ending in 1 is not a map ending in I. */
export function splitMapName(value: string): MapNameCandidate {
  const match = value.match(/^(.*[\u3400-\u9fff])\s*([0-9IVXLCDM!丨|li\u2160-\u217f]+)$/)
  if (!match) return { body: value, suffix: '', kind: 'none', value }
  const body = match[1]!, raw = match[2]!, suffix = normalizeRomanNumerals(raw)
  const kind = /^\d+$/.test(suffix) ? 'arabic' : romanNumeralPattern.test(suffix) ? 'roman' : 'unknown'
  const formatted = kind === 'roman' ? formatRomanNumeral(suffix) : suffix
  return { body, suffix, kind, value: body + formatted }
}

export function parseMapNameText(text: string): string | null {
  const normalized = normalizeRomanNumerals(text).normalize('NFKC').replace(/[\r\n\u2028\u2029]+/g, '')
    // Join only a trailing number to its name, not arbitrary neighboring labels.
    .replace(/([\u3400-\u9fff])\s*([0-9IVXLCDM!丨|li][0-9IVXLCDM!丨|li\s]*)(?=[【】,，。:：;；]*$)/g,
      (_match, last: string, suffix: string) => last + suffix.replace(/\s/g, ''))
    .replace(/([\u3400-\u9fff][0-9IVXLCDM!丨|li]*)[\s]*[【】,，。:：;；]+$/g, '$1')
  // Pipes within a label are separators. A trailing pipe remains pixel evidence.
  const parts = normalized.replace(/[【】]/g, ' ').replace(/[|｜](?=.*[\u3400-\u9fff])/g, ' ').split(/\s+/)
    .map(part => part.replace(/^[,，。:：;；]+|[,，。:：;；]+$/g, ''))
    .filter(part => part.length >= 2 && part.length <= 32 && !/^(?:小地图|大地图|地图)$/.test(part))
    .filter(part => /[\u3400-\u9fffA-Za-z]/.test(part) && !/^[IVXLCDM!丨|li\d]+$/.test(part))
  const last = parts.at(-1)
  return last ? splitMapName(last).value : null
}

/** Only reunite OCR boxes on the same baseline. Never append another UI row. */
export function mapNameFromReading(reading: OcrReading): string | null {
  if (!reading.items?.length) return parseMapNameText(reading.text)
  const items = reading.items.map(item => {
    const xs = item.polygon.map(p => p[0]), ys = item.polygon.map(p => p[1])
    return { ...item, x: Math.min(...xs), right: Math.max(...xs), y: Math.min(...ys), bottom: Math.max(...ys) }
  }).filter(item => item.polygon.length && item.bottom > item.y)
  const names = items.filter(item => /[\u3400-\u9fff]/.test(item.text) && parseMapNameText(item.text))
  const name = names.sort((a, b) => b.y - a.y)[0]
  if (!name) return parseMapNameText(reading.text)
  const height = name.bottom - name.y
  const tail = items.filter(item => item !== name && item.x >= name.right - height * .15
    && item.x - name.right <= height * 1.5
    && Math.abs(item.bottom - name.bottom) <= height * .35
    && /^[\s0-9IVXLCDM!丨|li]+$/.test(normalizeRomanNumerals(item.text).normalize('NFKC')))
    .sort((a, b) => a.x - b.x)
  return parseMapNameText(name.text + tail.map(item => item.text.trim()).join(''))
}
